import { Fragment, useEffect, useRef, useState, useContext } from "react";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassFoldUdBox, KeyValueLine, KeyValuesBoxGrid, KeyValuesHorizontal, PropertyTextDoc, RenderLinkToPropertyInfo, TooltipWrapper, ValueGroup, ViewButtonIcon } from "./PropertyComponents";
import { GlassButton } from "src/components/GlassButtons";
import { PropertyData, ViamapTingBogAllItem } from "./PropertyInfoCollectorTypes";
import {
   getServitutMainDocumentFileName,
   getTingbogsDokLink,
   ServitutTingbogPdfActions,
   TingBogRecordNumber,
} from "./PropertyInfoGroupFormatters";
import "./PropertyInfoGroupFormatters.css";
import { Spinner } from "react-bootstrap";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { GlassInfo } from "src/components/MitGlassComponents";
import { Persistence } from "src/managers/Persistence";
import { MapitStateContext, MapitStateActionType } from "src/states/MapitState";
import { PropertyInfoInterface } from 'src/managers/PropertyInfoInterface';
import { parse as WKTparse } from 'wkt';
import { GenerateGeomUtils } from 'src/managers/GenerateGeomUtils';
import { BFELookup } from 'src/managers/SearchBarGeneralPlugin';
import polylabel from 'polylabel';
import { DC } from "src/dataCollector/DataCollector";
import { message } from "src/components/Message";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { ServitutAnnotationsTable } from "src/modules/ANNModule/ANNComponents";
import { normalizeServitutIdentity } from "src/modules/ANNModule/ANNServitutIdentity";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { Feature } from "src/states/ApplicationStateFeatures";
import { BilagsVisning, type BilagAnnViewerContext } from "./SharedTLViewComponents";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import {
   collectPaataleberettigetLabels,
   formatFaerdselLabel,
   formatPartyDisplay,
   formatServitutTypeLabel,
   parseServitutDokumentXml,
   type ServitutDokumentParse,
} from "src/dataCollector/parseServitutDokumentXml";
import {
   filingTextIsSubstantial,
   formatPaategningFilingType,
   parseDokumentRevisionssporXml,
   resolveCurrentEasementText,
   type ServitutFilingTextGroup,
   type ServitutPaategningFiling,
} from "src/dataCollector/parseDokumentRevisionssporXml";
import { downloadTingbogDocument, fetchTingbogDokumentRevisionssporXml, isTingbogElectronicDocument } from "src/managers/TingbogDownload";

type ViamapTingBog = NonNullable<PropertyData["ViamapTingBog"]>;
type StamOplysninger = ViamapTingBog["stamOplysninger"];
type Servitut = ViamapTingBog["servitutter"][number] & { dokumentFilnavn?: string; senestPaategnetDato?: string };

function formatServitutDate(val?: string) {
   return val ? new Date(val).toLocaleDateString(Localization.getLocale(), { year: "numeric", month: "2-digit", day: "2-digit" }) : "";
}

function normalizeDatePart(val?: string | null) {
   return val?.split("T")[0] || "";
}

function originalServitutDate(servitut: Servitut, parsed?: ServitutDokumentParse | null): string {
   const alias = parsed?.alias || servitut.aktAliasId || servitut.aktId || "";
   const aliasDate = alias.split("-")[0] || "";
   if (/^\d{8}$/.test(aliasDate)) {
      return formatServitutDate(`${aliasDate.slice(0, 4)}-${aliasDate.slice(4, 6)}-${aliasDate.slice(6, 8)}`);
   }
   return servitut.tinglysningsDato ? formatServitutDate(normalizeDatePart(servitut.tinglysningsDato)) : "";
}

function resolveSenestPaategnetDato(
   servitut: Servitut,
   propertyData?: PropertyData,
   parsed?: ServitutDokumentParse | null,
): string {
   if (servitut.senestPaategnetDato) {
      return servitut.senestPaategnetDato;
   }
   const allItems = propertyData?.ViamapTingBogAll as ViamapTingBogAllItem[] | undefined;
   const match = allItems
      ?.flatMap((tingbog) => tingbog.servitutter || [])
      .find((item) => item.dokumentId && item.dokumentId === servitut.dokumentId);
   if (match?.senestPaategnetDato) {
      return match.senestPaategnetDato;
   }
   return parsed?.currentTinglysningsDato || parsed?.senesteAnmeldelse?.tidspunkt || "";
}
export function ServitutDetailsView(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);


   const servitut = props.managedWindow.windowParams?.servitut as Servitut | undefined;
   const stamOplysninger = props.managedWindow.windowParams?.stamOplysninger as StamOplysninger | undefined;
   const viamapTingBog = props.managedWindow.windowParams?.viamapTingBog as ViamapTingBog | undefined;
   const propertyData = props.managedWindow.windowParams?.propertyData as PropertyData | undefined;
   console.log("servitut", servitut);

   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [xmlDoc, setXmlDoc] = useState<Document | null>(null);
   const [paategninger, setPaategninger] = useState<ServitutPaategningFiling[]>([]);
   const [paategningLoading, setPaategningLoading] = useState(false);

   useEffect(() => {
      setXmlDoc(null);
      setError(null);
      setPaategninger([]);
      if (!servitut?.dokumentId) {
         setLoading(false);
         setPaategningLoading(false);
         return;
      }
      const abort = new AbortController();
      setLoading(true);
      setPaategningLoading(true);
      DC.TL.DokumentTingbog(servitut.dokumentId).then((data) => {
         if (abort.signal.aborted) return;
         setXmlDoc(data);
      }).catch((err) => {
         if (abort.signal.aborted) return;
         setXmlDoc(null);
         setError(err.message || String(err));
      }).finally(() => {
         if (!abort.signal.aborted) {
            setLoading(false);
         }
      });
      fetchTingbogDokumentRevisionssporXml(servitut.dokumentId, abort.signal).then((xml) => {
         if (abort.signal.aborted) return;
         setPaategninger(parseDokumentRevisionssporXml(xml));
      }).catch((err) => {
         if (abort.signal.aborted || (err instanceof Error && err.name === "AbortError")) return;
         setPaategninger([]);
      }).finally(() => {
         if (!abort.signal.aborted) {
            setPaategningLoading(false);
         }
      });
      return () => {
         abort.abort();
      };
   }, [servitut?.dokumentId]);

   if (!servitut) {
      return (
         <AdvancedDragModal
            window={props.managedWindow}
            title={Localization.getText("Servitutdetails")}
            onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         >
            <ADModalBody>
               <GlassInfo>
                  {Localization.getText("Missing context for servitut details") || "Missing context for servitut details"}
               </GlassInfo>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   let servitutTingbogLink = getTingbogsDokLink(servitut);
   const servitutIdentity = normalizeServitutIdentity(servitut);
   const annotationDocumentId = servitutIdentity.documentId;
   const aktIdForDatoloebenr = (servitutIdentity.aktAliasId && servitutIdentity.aktAliasId !== "") ? servitutIdentity.aktAliasId : servitutIdentity.aktId;
   const datoloebenr = servitutIdentity.datoloebenr || (aktIdForDatoloebenr ? TingBogRecordNumber(aktIdForDatoloebenr) : undefined);
   const mainDocumentFileName = getServitutMainDocumentFileName(servitut);
   const bilagAnnViewerContext: BilagAnnViewerContext | undefined = annotationDocumentId
      ? { servitutDocumentId: annotationDocumentId, datoloebenr, mainDocumentFileName }
      : undefined;
   const parsedDoc = xmlDoc ? parseServitutDokumentXml(xmlDoc) : null;
   const senestPaategnetDato = resolveSenestPaategnetDato(servitut, propertyData, parsedDoc);
   const originalDate = originalServitutDate(servitut, parsedDoc);
   const allPaatale = (propertyData?.ViamapTingBogAll as ViamapTingBogAllItem[] | undefined)
      ?.flatMap((tingbog) => tingbog.servitutter || [])
      .find((item) => item.dokumentId && item.dokumentId === servitut.dokumentId)
      ?.paataleberettiget || [];
   const paataleLabels = collectPaataleberettigetLabels(parsedDoc, [
      ...(servitut.paataleberettiget || []),
      ...allPaatale,
   ]);
   const tillaegTexts = parsedDoc?.tillaegstekster?.length
      ? parsedDoc.tillaegstekster
      : (servitut.tekster || []).filter(Boolean);
   const isScannedServitut = !isTingbogElectronicDocument(servitut);
   const isEmptyDigitalDocument = Boolean(parsedDoc?.isEmpty);
   const isDigitalServitut = !isScannedServitut && !isEmptyDigitalDocument && Boolean(parsedDoc);
   const forceAktDownload = isScannedServitut || isEmptyDigitalDocument;

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Servitutdetails")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         subHeaderContent={
            <>
               {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ? (
                  <>
                     <ViewButtonIcon onClick={() => console.info(xmlDoc)} ><MIBIcon icon="debugging" /><span style={{ paddingInline: "5px 5px" }} >Log to Console</span></ViewButtonIcon>
                  </>
               ) : null}
               <div className="tingbog-servitut-details-subheader">
                  <ServitutTingbogPdfActions
                     servitut={servitut}
                     stamOplysninger={stamOplysninger}
                     viamapTingBog={viamapTingBog}
                     propertyData={propertyData}
                     hasAccess={hasAccessToFeature}
                     xmlDoc={xmlDoc}
                     filings={paategninger}
                     filingsLoaded={!paategningLoading}
                     forceAktDownload={forceAktDownload}
                  />
               </div>
            </>
         }
      >
         <ADModalBody >
            <ServitutHeader
               servitut={servitut}
               parsedDoc={parsedDoc}
               originalDate={originalDate}
               senestPaategnetDato={senestPaategnetDato}
               revisionNummer={parsedDoc?.revisionNummer || servitut.revisionNummer}
               extraTexts={isDigitalServitut ? [] : tillaegTexts}
               paataleLabels={isDigitalServitut ? [] : paataleLabels}
            />
            {loading ? <ShowLoading /> : null}
            {error ? <ShowError /> : null}
            {xmlDoc ? <ShowTinglysningMessage xmlDoc={xmlDoc} link={servitutTingbogLink} /> : null}
            <div className="tingbog-servitut-details-stack">
               {isDigitalServitut ? (
                  <ServitutDokumentSection
                     servitut={servitut}
                     xmlDoc={xmlDoc}
                     parsedDoc={parsedDoc}
                     originalDate={originalDate}
                     datoloebenr={datoloebenr}
                     paataleLabels={paataleLabels}
                     filings={paategninger}
                     bilagAnnViewerContext={bilagAnnViewerContext}
                  />
               ) : xmlDoc ? (
                  <ServitutEjendomme servitutXDOC={xmlDoc} servitut={servitut} />
               ) : null}
               <ServitutPaategningSection
                  xmlDoc={xmlDoc}
                  datoloebenr={datoloebenr}
                  filings={paategninger}
                  bilagAnnViewerContext={bilagAnnViewerContext}
               />
               {xmlDoc ? (
                  <ProtectedFeature feature={Feature.AnnotationModule} contentsIfNoAccess={<></>}>
                     <ServitutAnnotationsTable
                        documentId={annotationDocumentId}
                        datoloebenr={datoloebenr}
                        servitut={servitut}
                        stamOplysninger={stamOplysninger}
                        xmlDoc={xmlDoc}
                        viamapTingBog={viamapTingBog}
                        propertyData={propertyData}
                     />
                  </ProtectedFeature>
               ) : null}
            </div>
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function ShowLoading() {
   let textRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (!textRef.current) return;
      const box = textRef.current;
      function updateText() {
         box.innerText = Localization.getText("The action takes longer than expected");
      }
      const interval = setTimeout(() => updateText(), 5000);
      box.innerText = Localization.getText("Loading");
      return () => {
         clearTimeout(interval);
      }
   }, [textRef]);

   return (
      <GlassInfo>
         <div ref={textRef} style={{ padding: 8 }} />
         <Spinner animation="border" role="status" />
      </GlassInfo>
   )
}

function ShowError() {
   return (
      <GlassInfo>
         <div style={{ padding: 8, color: "var(--dangerColor)" }}>{Localization.getText("Failed to fetch data")}</div>
      </GlassInfo>
   )
}

function ShowTinglysningMessage({ xmlDoc, link }: { xmlDoc: Document | null, link?: string }) {
   if (!(xmlDoc instanceof Document) || xmlDoc.children[0]?.children.length !== 0) {
      return <></>
   }
   function modifyTLMessage(link?: string, text?: string) {
      let textReplacment = link ? "\nServitut del af ældre dokumentsamling. Benyt download pdf for at hente denne" : "\nServitutten kan muligvis findes i ejendommens øvrige indskannet akt. Benyt download pdf knappen for at hente denne.";
      if (text) {
         return text.replace("Benyt i stedet forespørgsel i tingbøgerne.", textReplacment).trim();
      }
   }

   return (
      <GlassInfo>
         {modifyTLMessage(link, xmlDoc?.children[0].textContent)}

      </GlassInfo>
   )
}

function ServitutHeader({
   servitut,
   parsedDoc,
   originalDate,
   senestPaategnetDato,
   revisionNummer,
   extraTexts,
   paataleLabels,
}: {
   servitut: Servitut;
   parsedDoc: ServitutDokumentParse | null;
   originalDate: string;
   senestPaategnetDato: string;
   revisionNummer?: string;
   extraTexts?: string[];
   paataleLabels?: string[];
}) {
   const lastPåtegnetDateOnly = senestPaategnetDato ? formatServitutDate(normalizeDatePart(senestPaategnetDato)) : "";
   const shouldShowLastPåtegnet = Boolean(lastPåtegnetDateOnly) && lastPåtegnetDateOnly !== originalDate;
   const aktIdForDisplay = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
   const documentType = formatServitutTypeLabel(parsedDoc?.servitutType || servitut.servitutType);
   const faerdsel = formatFaerdselLabel(parsedDoc?.faerdselKode);
   const otherPropertyCount = parsedDoc && parsedDoc.propertyCount > 1 ? parsedDoc.propertyCount - 1 : 0;
   return (
      <KeyValuesHorizontal >
         <KeyValueLine valKey={Localization.getText("Date/serial number")} value={aktIdForDisplay ? TingBogRecordNumber(aktIdForDisplay) : ""} />
         {documentType ? <KeyValueLine valKey={Localization.getText("Document type")} value={documentType} /> : null}
         {originalDate ? <KeyValueLine valKey={Localization.getText("Registered")} value={originalDate} /> : null}
         {shouldShowLastPåtegnet ? <KeyValueLine valKey={<span style={{ color: "var(--dangerColor)" }}>{Localization.getText("Last signed")}</span>} value={lastPåtegnetDateOnly} /> : null}
         {revisionNummer && Number(revisionNummer) > 1 ? <KeyValueLine valKey={Localization.getText("Revision")} value={revisionNummer} /> : null}
         {faerdsel ? <KeyValueLine valKey={Localization.getText("Traffic right")} value={faerdsel} /> : null}
         {otherPropertyCount > 0 ? <KeyValueLine valKey={Localization.getText("Also registered on")} value={String(otherPropertyCount)} /> : null}
         {extraTexts?.map((text, idx) => (
            <KeyValueLine key={`tillaeg-${idx}`} valKey={Localization.getText("Additional text")} value={text} />
         ))}
         {paataleLabels?.map((label) => (
            <KeyValueLine key={label} valKey={Localization.getText("Entitled party")} value={label} />
         ))}
      </KeyValuesHorizontal >
   )
}

function ServitutFilingText({ groups, source }: { groups: ServitutFilingTextGroup[]; source?: string }) {
   if (!groups.length) {
      return null;
   }
   return (
      <PropertyTextDoc>
         <Fragment>
            {source ? <div className="tingbog-servitut-text-source">{source}</div> : null}
            {groups.map((group, index) => (
               <Fragment key={`${group.heading}-${index}`}>
                  {group.heading ? <h1>{group.heading}</h1> : null}
                  {group.paragraphs.map((paragraph, paragraphIndex) => (
                     <p key={paragraphIndex}>{paragraph}</p>
                  ))}
               </Fragment>
            ))}
         </Fragment>
      </PropertyTextDoc>
   );
}

function ServitutDownloadPdfLine({ onDownload, notice }: { onDownload: () => void | Promise<void>; notice?: string }) {
   const [busy, setBusy] = useState(false);

   return (
      <KeyValueLine
         _className="tingbog-servitut-action-line"
         valKey={Localization.getText("Download PDF")}
         value={
            <div className="tingbog-servitut-download-action">
               {notice ? (
                  <TooltipWrapper
                     title={notice}
                     className="ViewButtonToolTip tingbog-servitut-download-notice-tooltip"
                  >
                     <span className="tingbog-servitut-download-notice" aria-label={notice}>
                        <MIBIcon icon="info" />
                     </span>
                  </TooltipWrapper>
               ) : null}
               <GlassButton
                  disabled={busy}
                  onClick={(event) => {
                     event.preventDefault();
                     event.stopPropagation();
                     if (busy) {
                        return;
                     }
                     setBusy(true);
                     void Promise.resolve(onDownload()).finally(() => setBusy(false));
                  }}
               >
                  {busy ? (
                     <Spinner animation="border" size="sm" className="tingbog-download-spinner" />
                  ) : null}
                  {busy ? Localization.getText("Downloading...") : Localization.getText("Download PDF")}
               </GlassButton>
            </div>
         }
      />
   );
}

function ServitutDokumentSection({
   servitut,
   xmlDoc,
   parsedDoc,
   originalDate,
   datoloebenr,
   paataleLabels,
   filings,
   bilagAnnViewerContext,
}: {
   servitut: Servitut;
   xmlDoc: Document | null;
   parsedDoc: ServitutDokumentParse | null;
   originalDate: string;
   datoloebenr?: string;
   paataleLabels: string[];
   filings: ServitutPaategningFiling[];
   bilagAnnViewerContext?: BilagAnnViewerContext;
}) {
   const documentType = formatServitutTypeLabel(parsedDoc?.servitutType || servitut.servitutType);
   const tillaeg = parsedDoc?.tillaegstekster?.length
      ? parsedDoc.tillaegstekster
      : (servitut.tekster || []).filter(Boolean);
   const textGroups = resolveCurrentEasementText(xmlDoc, filings);
   const originalFiling = filings.find((filing) => filing.kind === "servitut");
   const anmelder = originalFiling?.anmelder || parsedDoc?.anmelder;
   const anmelderLabel = formatPartyDisplay(anmelder);
   const typeLabel = Localization.getText(formatPaategningFilingType("servitut") || "Servitut");
   const dokumentId = parsedDoc?.dokumentId || servitut.dokumentId || "";
   const paategningTexts = filings.filter((filing) => (
      filing.kind === "paategning" && filingTextIsSubstantial(filing.textGroups)
   ));

   return (
      <div className="tingbog-servitut-section">
         <GlassFoldUdBox title={Localization.getText("Current easement")} foldetUd={true}>
            <ValueGroup>
               <KeyValueLine valKey={Localization.getText("Date/serial number")} value={datoloebenr || ""} />
               {dokumentId ? (
                  <ServitutDownloadPdfLine
                     notice={Localization.getText("Current easement PDF notice")}
                     onDownload={() => downloadTingbogDocument(
                        {
                           kind: "gatewayDokumentPdf",
                           dokumentIdentifikator: dokumentId,
                        },
                        `servitut_${dokumentId}`,
                     )}
                  />
               ) : null}
               <KeyValueLine valKey={Localization.getText("Endorsement type")} value={typeLabel} />
               {documentType && documentType !== typeLabel ? (
                  <KeyValueLine valKey={Localization.getText("Document type")} value={documentType} />
               ) : null}
               {anmelderLabel ? <KeyValueLine valKey={Localization.getText("Applicant")} value={anmelderLabel} /> : null}
               {originalDate ? <KeyValueLine valKey={Localization.getText("Application date")} value={originalDate} /> : null}
               {paataleLabels.map((label) => (
                  <KeyValueLine key={label} valKey={Localization.getText("Entitled party")} value={label} />
               ))}
               {tillaeg.map((text, idx) => (
                  <KeyValueLine key={`tillaeg-${idx}`} valKey={Localization.getText("Additional text")} value={text} />
               ))}
            </ValueGroup>
            <ServitutFilingText groups={textGroups} />
            {paategningTexts.map((filing) => {
               const typeLabelForFiling = Localization.getText(formatPaategningFilingType(filing.kind) || "Endorsement");
               const filedDate = formatServitutDate(normalizeDatePart(filing.tidspunkt));
               return (
                  <ServitutFilingText
                     key={filing.id || filing.tidspunkt}
                     groups={filing.textGroups}
                     source={`${Localization.getText("From filing")}: ${[filedDate, typeLabelForFiling].filter(Boolean).join(" · ")}`}
                  />
               );
            })}
            {xmlDoc ? (
               <BilagsVisning
                  servitutXDOC={xmlDoc}
                  foldetUd={false}
                  bilagAnnViewerContext={bilagAnnViewerContext}
               />
            ) : null}
            {xmlDoc ? <ServitutEjendomme servitutXDOC={xmlDoc} servitut={servitut} /> : null}
         </GlassFoldUdBox>
      </div>
   );
}

function ServitutPaategningSection({
   xmlDoc,
   datoloebenr,
   filings,
   bilagAnnViewerContext,
}: {
   xmlDoc: Document | null;
   datoloebenr?: string;
   filings: ServitutPaategningFiling[];
   bilagAnnViewerContext?: BilagAnnViewerContext;
}) {
   const hasLaterFiling = filings.some((filing) => filing.kind !== "servitut");
   if (!hasLaterFiling) {
      return null;
   }

   return (
      <div className="tingbog-servitut-section">
         <GlassFoldUdBox title={Localization.getText("Endorsement details")} foldetUd={false}>
            {filings.map((filing) => {
               const typeLabel = Localization.getText(formatPaategningFilingType(filing.kind) || "Endorsement");
               const anmelderLabel = formatPartyDisplay(filing.anmelder);
               const filedDate = formatServitutDate(normalizeDatePart(filing.tidspunkt));
               return (
                  <GlassFoldUdBox
                     key={filing.id || filing.tidspunkt}
                     title={[filedDate, typeLabel].filter(Boolean).join(" · ")}
                     foldetUd={false}
                  >
                     <ValueGroup>
                        <KeyValueLine valKey={Localization.getText("Date/serial number")} value={datoloebenr || ""} />
                        <KeyValueLine valKey={Localization.getText("Endorsement type")} value={typeLabel} />
                        {anmelderLabel ? <KeyValueLine valKey={Localization.getText("Applicant")} value={anmelderLabel} /> : null}
                        {filedDate ? <KeyValueLine valKey={Localization.getText("Application date")} value={filedDate} /> : null}
                     </ValueGroup>
                     <ServitutFilingText groups={filing.textGroups} />
                     {filing.bilagRefs.length ? (
                        <BilagsVisning
                           servitutXDOC={xmlDoc}
                           includeRefs={filing.bilagRefs}
                           foldetUd={true}
                           bilagAnnViewerContext={bilagAnnViewerContext}
                        />
                     ) : null}
                  </GlassFoldUdBox>
               );
            })}
         </GlassFoldUdBox>
      </div>
   );
}

function ServitutEjendomme({ servitutXDOC, servitut }: { servitutXDOC: Document | null, servitut: Servitut }) {
   const [Ejendomme, setEjendomme] = useState<Element[]>(Array.from(servitutXDOC?.getElementsByTagName("FastEjendomMedRettigheder") ?? []));

   useEffect(() => {
      const abortController = new AbortController();
      async function fetchEjendomme(soeg: Parameters<typeof DC.TL.EjendomSoeg>[0]) {
         DC.TL.EjendomSoeg(soeg).then((data) => {
            if (abortController.signal.aborted) return;
            console.log("fetchEjendomme response", data);
            if (data instanceof Document) {
               const ejendomme = Array.from(data.getElementsByTagName("EjendomIdentifikator"));
               setEjendomme(ejendomme);
               console.log("Fetched Ejendomme for servitut", ejendomme);
            } else {
               console.warn("Unexpected response format from EjendomSoeg, expected XML Document", data);
            }
         });
      }

      // READY
      // oxlint-disable-next-line no-constant-condition
      if (Ejendomme.length === 0 && servitutXDOC instanceof Document) {
         switch (true) {
            case "dokumentId" in servitut && Boolean(servitut.dokumentId): {
               fetchEjendomme({ dokumentID: servitut.dokumentId });
               break;
            }
            case "aktAliasId" in servitut && Boolean(servitut.aktAliasId): {
               fetchEjendomme({ dokumentAliasHist: servitut.aktAliasId });
               break;
            }
            case "aktId" in servitut && Boolean(servitut.aktId): {
               fetchEjendomme({ dokumentAlias: servitut.aktId });
               break;
            }
         }
      }
   }, [servitut])

   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const [creatingLayer, setCreatingLayer] = useState(false);
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }

   if (Ejendomme.length === 0) {
      return <></>
   }

   async function handleAddAsLayer() {
      setCreatingLayer(true);
      try {
         const polygonFeatures: any[] = [];
         const markerFeatures: any[] = [];
         const missingAddressBFEs: string[] = [];
         const bfeLookupInstance = new BFELookup(() => { });

         for (const ejendom of Ejendomme) {
            const bfe = ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || null;
            if (!bfe) {
               throw new Error('Servitut: missing BFE number in FastEjendomMedRettigheder element');
            }

            const aktIdForLabel = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
            const servitutLabel = aktIdForLabel ? TingBogRecordNumber(aktIdForLabel) : "";
            const baseProps: any = {
               servitutId: servitut?.dokumentId,
               servitutdatoløbenummer: servitutLabel,
               BFEnummer: bfe
            };

            // Adds polygons and markers to their lists for making layers later.
            const idNum = Number(bfe);
            if (!Number.isNaN(idNum)) {
               const suggestions = await bfeLookupInstance.calculateSuggestions({}, bfe.toString());
               if (suggestions.length === 1) {
                  const bfeWithoutSFE = suggestions.filter(s => s?.data?.type === 'Ejerlejlighed' || s?.data?.type === 'BPFG');
                  if (bfeWithoutSFE.length === 0) { //SFE
                     try {
                        const sfeGeo = await PropertyInfoInterface.getGeojsonOfSFE(idNum);
                        const sfeFeatures = (sfeGeo && Array.isArray(sfeGeo.features)) ? sfeGeo.features : [];
                        for (const sf of sfeFeatures) {
                           if (sf?.geometry && (sf.geometry.type === 'Polygon' || sf.geometry.type === 'MultiPolygon')) {
                              polygonFeatures.push({ type: 'Feature', geometry: sf.geometry, properties: { ...baseProps, ...sf.properties } });
                           }
                        }
                     } catch (e) {
                        console.warn('Servitut: SFE polygon fetch error (skipping polygons) for', bfe, e);
                     }
                  }
                  else { //Ejerlejighed/BPFG
                     const coords = await handleCoordinates(bfeWithoutSFE[0].data);
                     if (!coords) {
                        missingAddressBFEs.push(bfe);
                        continue;
                     }
                     const propsBase = { BFEnummer: bfe };
                     markerFeatures.push({
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] },
                        properties: { ...propsBase, Ejendomstype: bfeWithoutSFE[0].data.type }
                     });
                  }
               }
               else {
                  throw new Error('Servitut: multiple BFELookup suggestions for BFE number in FastEjendomMedRettigheder element: ' + bfe);
               }
            } else {
               throw new Error('Servitut: invalid BFE number in FastEjendomMedRettigheder element: ' + bfe);
            }
         }

         // Inform the user about BFEs without address, but continue with layers for others
         if (missingAddressBFEs.length > 0) {
            message.info(Localization.getText("One or more properties have no address. BFE") + ": " + missingAddressBFEs.join(', '));
         }

         if (polygonFeatures.length === 0 && markerFeatures.length === 0) {
            message.error('no geometries found (no polygons, no points)');
            return;
         }

         const aktIdForDatasetLabel = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
         const label = aktIdForDatasetLabel ? TingBogRecordNumber(aktIdForDatasetLabel) : "";
         const baseDatasetName = Localization.getText("Record") + ` ${label} ` + Localization.getText("registered");

         if (polygonFeatures.length > 0) {
            const polyFc = { type: 'FeatureCollection', features: polygonFeatures };
            Persistence.createLayersFromGeoJSON(`${baseDatasetName} - polygoner`, polyFc as any, new Date(), (li: any) => {
               mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } });
            });
         }

         if (markerFeatures.length > 0) {
            const markerFc = { type: 'FeatureCollection', features: markerFeatures };
            Persistence.createLayersFromGeoJSON(`${baseDatasetName} - punkter`, markerFc as any, new Date(), (li: any) => {
               mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } });
            });
         }

      } catch (err: any) {
         message.error(err.message || String(err));
      } finally {
         setCreatingLayer(false);
      }
   }

   return (
      <GlassFoldUdBox
         title={
            <>
               <span style={{ marginRight: 'auto' }}>{Localization.getText("Properties") + " (" + Ejendomme.length + ")"}</span>
               {creatingLayer ? <Spinner animation="border" size="sm" role="status" style={{ marginRight: 8 }} /> : null}
               <GlassButton
                  disabled={creatingLayer}
                  onMouseDown={(e: any) => { e.stopPropagation(); }}
                  onClick={(e: any) => { e.stopPropagation(); void handleAddAsLayer(); }}
               >
                  {Localization.getText("Add as layer")}
               </GlassButton>
            </>
         }
         foldetUd={false}>
         {Ejendomme.map((ejendom, index) => {
            const bfeText = ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || "";
            const bfeNr = parseInt(bfeText, 10);
            return (
            <Fragment key={index}>
               <ValueGroup>
                  <KeyValueLine
                     valKey={"BFEnummer"}
                     value={bfeNr && !Number.isNaN(bfeNr) ? RenderLinkToPropertyInfo(bfeText, { bfeNr }) : bfeText}
                  />
                  <KeyValuesBoxGrid minWidth={"150px"} >
                     {Array.from(ejendom.getElementsByTagName("Matrikel")).map((matrikel, idx) => {
                        const name = matrikel.getElementsByTagName("CadastralDistrictName")[0]?.textContent || "";
                        const matrNr = String(matrikel.getElementsByTagName("Matrikelnummer")[0]?.textContent ?? "")?.replace(/^0*/, "");
                        return (
                           <Fragment key={idx}>
                              <KeyValueLine valKey={"Matnr. " + matrNr} value={name} keepEmpty />
                           </Fragment>
                        )
                     })}
                  </KeyValuesBoxGrid>
               </ValueGroup>
            </Fragment>
            );
         })}
      </GlassFoldUdBox>
   )
}

function ServitutTekstGrupper({ servitutXDOC }: { servitutXDOC: Element }) {
   let header = servitutXDOC.getElementsByTagName("Overskrift")[0];
   let paragraphs = servitutXDOC.getElementsByTagName("Afsnit");
   return (<>
      {header ? (
         <h1>
            {header.textContent}
         </h1>
      ) : null}
      {[...paragraphs].map((p, idx) => (
         <p key={idx} >
            {p.textContent}
         </p>
      ))}
   </>
   )
}

function ServitutTekst({ servitutXDOC, plain = false }: { servitutXDOC: Document | null; plain?: boolean }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let servitut = servitutXDOC.getElementsByTagName?.("Servitut")[0];
   let servitutTextGroups = [...servitut?.getElementsByTagName("TekstGruppe") || []];
   if (servitutTextGroups.length === 0) {
      return <></>
   }
   let firstTextGroup = servitutTextGroups[0];
   let header = firstTextGroup.getElementsByTagName("Overskrift")[0];
   let paragraphs = firstTextGroup.getElementsByTagName("Afsnit");
   const body = (
      <PropertyTextDoc>
         {servitutTextGroups.map((tg, idx) => {
            return (
               <Fragment key={idx}>
                  {(plain || tg.children.length > 1 || idx != 0) && (
                     <ServitutTekstGrupper key={idx} servitutXDOC={tg} />
                  )}
               </Fragment>
            )
         })}
      </PropertyTextDoc>
   );
   if (plain) {
      return body;
   }
   return (
      <GlassFoldUdBox title={"Servitut - " + (header?.textContent || paragraphs[0]?.textContent)} foldetUd={true}>
         {body}
      </GlassFoldUdBox>
   )
}


const handleCoordinates = async (data: {
   BFEnummer: number,
   SFEnummer?: string | number, // finde center of sfe, 
   type: "SFE" | "BPFG" | "Ejerlejlighed",
   geometri?: string, //WKT string
}): Promise<any> => {
   if (data.geometri) {
      let tmp = WKTparse(data.geometri)
      if (tmp?.coordinates[0]) {
         let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
         return coords;
      }
   }
   if ("geometri" in data) {
      delete data.geometri;
   }
   if (data.type) {
      let EBR = await DC.DF_GQL.EBR.EBRAdresseOgPunkt(String(data.BFEnummer));
      let geometri = EBR[0]?.geometri;
      if (geometri) {
         let tmp = WKTparse(geometri)
         if (tmp?.coordinates[0]) {
            let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
            return coords;
         }
      }
   }
   if (data.type == "SFE") {
      let geoJson = await PropertyInfoInterface.getGeojsonOfSFE(data.BFEnummer);
      let coords = polylabel(geoJson.features[0].geometry.coordinates, 0.00001)
      return coords;
   }
};
